import type { DeviceHealthStatus } from '@pos/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { FormPanel } from '../../components/FormPanel';
import { Input } from '../../components/Input';
import { KpiCard } from '../../components/KpiCard';
import { Modal } from '../../components/Modal';
import { Pill } from '../../components/Pill';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import { listBranches } from '../../data/fetchers';
import { dashboardApi } from '../../lib/apiClient';
import { fmtInt } from '../../lib/format';
import { visibleBranchIds } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { useUi } from '../../stores/ui';
import { OperationsHeader } from './OperationsHeader';

const STATUS_DISPLAY: Record<
  DeviceHealthStatus['status'],
  { label: string; tone: 'ok' | 'warn' | 'danger' }
> = {
  online: { label: 'ออนไลน์', tone: 'ok' },
  stale: { label: 'ขาดการติดต่อ', tone: 'warn' },
  offline: { label: 'ออฟไลน์', tone: 'danger' },
};

const CREDENTIAL_STATE = {
  active: { label: 'พร้อมใช้งาน', tone: 'ok' },
  refresh_required: { label: 'ต้องกู้คืน', tone: 'warn' },
  revoked: { label: 'เพิกถอนแล้ว', tone: 'danger' },
} as const;

function fmtDateTime(value: string | null): string {
  if (!value) {
    return 'ยังไม่เคยซิงก์';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('th-TH', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function DeviceHealthView() {
  const queryClient = useQueryClient();
  const branchId = useUi((state) => state.branchId);
  const sessionUser = useSession((state) => state.user);
  const { data, isLoading } = useQuery({
    queryKey: ['device-health', branchId],
    queryFn: () =>
      dashboardApi.getDeviceHealth({
        branchId: branchId ?? undefined,
      }),
  });
  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });

  const [label, setLabel] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState(branchId ?? '');
  const [enrollCode, setEnrollCode] = useState('');
  const [enrollError, setEnrollError] = useState('');
  const [enrollment, setEnrollment] = useState<{
    code: string;
    expiresAt: string;
  } | null>(null);
  const [detailDeviceId, setDetailDeviceId] = useState<string | null>(null);
  const detailDevice =
    data?.devices.find((device) => device.deviceId === detailDeviceId) ?? null;
  const [selectedAction, setSelectedAction] = useState<{
    kind: 'revoke' | 'recovery';
    deviceId: string;
    label: string;
  } | null>(null);
  const [actionFeedback, setActionFeedback] = useState<{
    kind: 'revoke' | 'recovery';
    deviceId: string;
    error?: string;
    result?: { code: string; expiresAt: string };
  } | null>(null);

  const canManageDevices =
    sessionUser?.role === 'owner' || sessionUser?.role === 'manager';
  const canCreateDevice = canManageDevices;
  const canManageCredentials = canManageDevices;
  const visibleBranches = useMemo(() => {
    if (!sessionUser) return [];
    const allowedIds = visibleBranchIds(
      sessionUser,
      branches.map((branch) => branch.id),
    );
    return branches.filter((branch) => allowedIds.includes(branch.id));
  }, [branches, sessionUser]);
  const manageableBranchIds = useMemo(
    () =>
      sessionUser
        ? new Set(
            visibleBranchIds(
              sessionUser,
              data?.devices.map((device) => device.branchId) ?? [],
            ),
          )
        : new Set<string>(),
    [sessionUser, data?.devices],
  );

  const revokeMutation = useMutation({
    mutationFn: (deviceId: string) => dashboardApi.revokeDevice(deviceId),
    onMutate: (deviceId) => {
      setActionFeedback({ kind: 'revoke', deviceId });
    },
    onSuccess: async (_result, deviceId) => {
      setActionFeedback(null);
      setSelectedAction((current) =>
        current?.kind === 'revoke' && current.deviceId === deviceId
          ? null
          : current,
      );
      await queryClient.invalidateQueries({ queryKey: ['device-health'] });
    },
    onError: (error, deviceId) => {
      setActionFeedback({
        kind: 'revoke',
        deviceId,
        error: error instanceof Error ? error.message : 'เพิกถอนไม่สำเร็จ',
      });
    },
  });
  const recoveryMutation = useMutation({
    mutationFn: (input: { deviceId: string; label: string }) =>
      dashboardApi.createDeviceRecoveryCode(input.deviceId, {
        label: input.label,
      }),
    onMutate: ({ deviceId }) => {
      setActionFeedback({ kind: 'recovery', deviceId });
    },
    onSuccess: async (result, { deviceId }) => {
      setActionFeedback({
        kind: 'recovery',
        deviceId,
        result: { code: result.code, expiresAt: result.expiresAt },
      });
      await queryClient.invalidateQueries({ queryKey: ['device-health'] });
    },
    onError: (error, { deviceId }) => {
      setActionFeedback({
        kind: 'recovery',
        deviceId,
        error:
          error instanceof Error ? error.message : 'สร้างรหัสกู้คืนไม่สำเร็จ',
      });
    },
  });
  const deviceMutationPending =
    revokeMutation.isPending || recoveryMutation.isPending;
  const attentionCount = (data?.staleCount ?? 0) + (data?.offlineCount ?? 0);

  const createCodeMutation = useMutation({
    mutationFn: () =>
      dashboardApi.createDeviceEnrollment({
        branchId: selectedBranchId,
        label: label.trim(),
      }),
    onSuccess: (result) => {
      setEnrollCode(result.code);
      setEnrollment({ code: result.code, expiresAt: result.expiresAt });
    },
    onError: (error) => {
      setEnrollError(
        error instanceof Error
          ? error.message || 'สร้างรหัสไม่สำเร็จ'
          : 'สร้างรหัสไม่สำเร็จ',
      );
    },
  });

  useEffect(() => {
    setSelectedBranchId((current) => {
      if (
        branchId &&
        visibleBranches.some((branch) => branch.id === branchId)
      ) {
        return branchId;
      }
      if (visibleBranches.some((branch) => branch.id === current)) {
        return current;
      }
      return visibleBranches[0]?.id ?? '';
    });
  }, [branchId, visibleBranches]);

  const columns = useMemo<ColumnDef<DeviceHealthStatus, unknown>[]>(
    () => [
      { accessorKey: 'deviceId', header: 'เครื่อง' },
      { accessorKey: 'branchId', header: 'สาขา' },
      {
        accessorKey: 'status',
        header: 'สถานะ',
        cell: (cell) => {
          const status = STATUS_DISPLAY[cell.row.original.status];
          return <Pill tone={status.tone}>{status.label}</Pill>;
        },
      },
      {
        accessorKey: 'credentialState',
        header: 'ข้อมูลรับรอง',
        cell: (cell) => {
          const state = CREDENTIAL_STATE[cell.row.original.credentialState];
          return <Pill tone={state.tone}>{state.label}</Pill>;
        },
      },
      {
        accessorKey: 'pendingEventCount',
        header: 'รอส่ง',
        cell: (cell) => (
          <span className="num">{fmtInt(cell.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'failedEventCount',
        header: 'ไม่สำเร็จ',
        cell: (cell) => (
          <span className="num">{fmtInt(cell.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'dlqCount',
        header: 'DLQ',
        cell: (cell) => (
          <span className="num">{fmtInt(cell.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'currentMasterDataVersion',
        header: 'ข้อมูลล่าสุด',
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <span className="num">
              {fmtInt(row.currentMasterDataVersion)} /{' '}
              {fmtInt(row.latestMasterDataVersion)}
            </span>
          );
        },
      },
      {
        accessorKey: 'lastSeenAt',
        header: 'ล่าสุด',
        cell: (cell) => fmtDateTime(cell.getValue<string | null>()),
      },
      {
        accessorKey: 'appVersion',
        header: 'แอป',
        cell: (cell) => cell.getValue<string | null>() ?? '—',
      },
    ],
    [
      canManageCredentials,
      deviceMutationPending,
      manageableBranchIds,
      sessionUser?.role,
    ],
  );

  return (
    <div className="bo-fade">
      <OperationsHeader
        title="เครื่อง POS"
        subtitle="ดูว่าเครื่องไหนพร้อมขาย เครื่องไหนขาดการติดต่อ และจัดการสิทธิ์ของแต่ละเครื่องจากจุดเดียว"
        status={
          isLoading
            ? { label: 'กำลังตรวจสอบสถานะ', tone: 'neutral' }
            : attentionCount > 0
              ? {
                  label: `มี ${fmtInt(attentionCount)} เครื่องที่ควรตรวจสอบ`,
                  tone: data?.offlineCount ? 'danger' : 'warn',
                }
              : { label: 'ทุกเครื่องทำงานปกติ', tone: 'ok' }
        }
      />
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <KpiCard
          label="เครื่องทั้งหมด"
          value={isLoading ? '…' : fmtInt(data?.totalDevices ?? 0)}
          sub="เครื่องในสาขาที่คุณดูแล"
        />
        <KpiCard
          label="ออนไลน์"
          value={isLoading ? '…' : fmtInt(data?.onlineCount ?? 0)}
          sub="พร้อมรับและส่งข้อมูล"
          tone="ok"
        />
        <KpiCard
          label="ขาดการติดต่อ"
          value={isLoading ? '…' : fmtInt(data?.staleCount ?? 0)}
          sub="ควรตรวจการเชื่อมต่อ"
          tone="warn"
        />
        <KpiCard
          label="ออฟไลน์"
          value={isLoading ? '…' : fmtInt(data?.offlineCount ?? 0)}
          sub="ตรวจเครื่องและการเชื่อมต่อ"
          tone="danger"
        />
      </div>
      {canCreateDevice ? (
        <FormPanel className="mb-6">
          <h2 className="text-h3 font-bold text-ink mb-4">เพิ่มเครื่อง POS</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="สาขา">
              <Select
                id="activation-branch"
                value={selectedBranchId}
                onChange={(event) => setSelectedBranchId(event.target.value)}
                disabled={branchesLoading || visibleBranches.length === 0}
              >
                {visibleBranches.length === 0 ? (
                  <option value="">
                    {branchesLoading ? 'กำลังโหลดสาขา' : 'ไม่พบสาขาที่เลือกได้'}
                  </option>
                ) : null}
                {visibleBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.short}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="ชื่อเครื่อง">
              <Input
                id="activation-name"
                placeholder="เช่น เคาน์เตอร์หน้าโรงอาหาร"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            </Field>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Btn
              kind="primary"
              type="button"
              onClick={() => {
                setEnrollError('');
                setEnrollCode('');
                setEnrollment(null);
                createCodeMutation.mutate();
              }}
              disabled={createCodeMutation.isPending || !selectedBranchId}
            >
              {createCodeMutation.isPending ? 'กำลังสร้าง' : 'สร้างรหัส'}
            </Btn>
            {enrollment && (
              <span className="font-mono text-xl tracking-[0.1em] text-ink">
                รหัส: {enrollCode}
              </span>
            )}
          </div>
          {enrollError && (
            <p className="mt-3 text-sm-label text-danger">{enrollError}</p>
          )}
        </FormPanel>
      ) : null}
      <DataTable
        pageSize={Number.MAX_SAFE_INTEGER}
        columns={columns}
        data={data?.devices ?? []}
        mobileCards
        onRowClick={(device) => setDetailDeviceId(device.deviceId)}
      />
      <Modal
        variant="drawer"
        className="!my-0"
        open={!!detailDevice}
        title="รายละเอียดเครื่อง POS"
        onClose={() => setDetailDeviceId(null)}
      >
        {detailDevice && (
          <div className="space-y-4">
            <dl className="space-y-3">
              {[
                ['หมายเลขเครื่อง', detailDevice.deviceId],
                ['ชื่อเครื่อง', detailDevice.label],
                ['สาขา', detailDevice.branchId],
                ['สถานะ', STATUS_DISPLAY[detailDevice.status].label],
                [
                  'ข้อมูลรับรอง',
                  CREDENTIAL_STATE[detailDevice.credentialState].label,
                ],
                ['รอส่ง', fmtInt(detailDevice.pendingEventCount)],
                ['ไม่สำเร็จ', fmtInt(detailDevice.failedEventCount)],
                ['DLQ', fmtInt(detailDevice.dlqCount)],
                [
                  'ข้อมูลล่าสุด',
                  `${detailDevice.currentMasterDataVersion} / ${detailDevice.latestMasterDataVersion}`,
                ],
                ['เชื่อมต่อล่าสุด', fmtDateTime(detailDevice.lastSeenAt)],
                ['เวอร์ชันแอป', detailDevice.appVersion ?? '—'],
              ].map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-ink-3">{label}</dt>
                  <dd className="break-words font-medium">{value}</dd>
                </div>
              ))}
            </dl>
            {canManageCredentials &&
              (sessionUser?.role === 'owner' ||
                manageableBranchIds.has(detailDevice.branchId)) && (
                <div className="flex flex-wrap gap-2">
                  <Btn
                    kind="danger"
                    disabled={deviceMutationPending}
                    aria-label={`เพิกถอนเครื่อง ${detailDevice.deviceId}`}
                    onClick={() => {
                      setActionFeedback(null);
                      setSelectedAction({
                        kind: 'revoke',
                        deviceId: detailDevice.deviceId,
                        label: detailDevice.label,
                      });
                    }}
                  >
                    เพิกถอน
                  </Btn>
                  <Btn
                    disabled={deviceMutationPending}
                    aria-label={`กู้คืนเครื่อง ${detailDevice.deviceId}`}
                    onClick={() => {
                      setActionFeedback(null);
                      setSelectedAction({
                        kind: 'recovery',
                        deviceId: detailDevice.deviceId,
                        label: detailDevice.label,
                      });
                    }}
                  >
                    กู้คืน
                  </Btn>
                </div>
              )}
          </div>
        )}
      </Modal>
      <Modal
        className="!my-auto"
        open={Boolean(selectedAction)}
        onClose={() => setSelectedAction(null)}
        title={
          selectedAction?.kind === 'revoke'
            ? `ยืนยันเพิกถอนเครื่อง ${selectedAction.deviceId}`
            : selectedAction
              ? `ยืนยันกู้คืนเครื่อง ${selectedAction.deviceId}`
              : undefined
        }
        description={
          selectedAction?.kind === 'revoke'
            ? 'ตรวจสอบผลกระทบก่อนยืนยัน การดำเนินการนี้จะหยุดการเชื่อมต่อกับระบบกลาง'
            : 'ระบบจะสร้างรหัสใหม่สำหรับเชื่อมเครื่องนี้กลับเข้ากับสาขาเดิม'
        }
        tone={selectedAction?.kind === 'revoke' ? 'danger' : 'default'}
        footer={
          selectedAction ? (
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Btn
                type="button"
                className="w-full sm:w-auto"
                disabled={deviceMutationPending}
                onClick={() => setSelectedAction(null)}
              >
                {actionFeedback?.result ? 'ปิด' : 'ยกเลิก'}
              </Btn>
              {actionFeedback?.result ? null : (
                <Btn
                  kind={selectedAction.kind === 'revoke' ? 'danger' : 'primary'}
                  type="button"
                  className="w-full sm:w-auto"
                  disabled={deviceMutationPending}
                  onClick={() => {
                    if (selectedAction.kind === 'revoke') {
                      revokeMutation.mutate(selectedAction.deviceId);
                      return;
                    }
                    recoveryMutation.mutate({
                      deviceId: selectedAction.deviceId,
                      label: selectedAction.label,
                    });
                  }}
                >
                  {selectedAction.kind === 'revoke'
                    ? revokeMutation.isPending
                      ? 'กำลังเพิกถอน'
                      : 'ยืนยันเพิกถอน'
                    : recoveryMutation.isPending
                      ? 'กำลังสร้างรหัสกู้คืน'
                      : 'ยืนยันสร้างรหัสกู้คืน'}
                </Btn>
              )}
            </div>
          ) : null
        }
      >
        {selectedAction ? (
          <div aria-live="polite" className="space-y-4">
            {selectedAction.kind === 'revoke' ? (
              <div className="rounded-lg border border-danger/20 bg-danger/5 p-4">
                <div className="font-bold text-danger">ผลหลังเพิกถอน</div>
                <p className="mt-1 text-body text-ink-2">
                  ซิงก์คลาวด์จะหยุด แต่ยอดขายออฟไลน์ยังอยู่ในเครื่อง
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-line bg-surface-2/40 p-4">
                <div className="text-sm-label text-ink-3">เครื่อง</div>
                <div className="mt-1 font-bold text-ink">
                  {selectedAction.label} · {selectedAction.deviceId}
                </div>
              </div>
            )}
            {actionFeedback?.kind === selectedAction.kind &&
            actionFeedback.deviceId === selectedAction.deviceId &&
            actionFeedback.error ? (
              <p role="alert" className="text-danger">
                {actionFeedback.error}
              </p>
            ) : null}
            {actionFeedback?.kind === 'recovery' &&
            actionFeedback.deviceId === selectedAction.deviceId &&
            actionFeedback.result ? (
              <div className="rounded-lg border border-ok/20 bg-ok/5 p-4">
                <div className="text-sm-label font-bold text-ok">
                  สร้างรหัสกู้คืนแล้ว
                </div>
                <div className="mt-2 font-mono text-2xl font-bold tracking-[0.12em] text-ink">
                  {actionFeedback.result.code}
                </div>
                <p className="mt-2 text-sm-label text-ink-3">
                  หมดอายุ {fmtDateTime(actionFeedback.result.expiresAt)}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
